import { SlugFilter } from '@providers/types/api-responses'
import i18n from '@utils/i18n'

import { SUBFACETS, FILTERS, MUSIC_TYPE_ORDER } from './constants'
import { Aggregation, Aggregations, AggregationTerm, Facet, FacetTerm } from './types'

export const getTerms = (aggregations: Aggregation[]): FacetTerm[] => {
  return aggregations
    .reduce((facets, facet) => {
      const { doc_count, key } = facet
      if (!doc_count) return facets
      return [...facets, { key, count: facet.doc_count }]
    }, [] as FacetTerm[])
    .sort((a, b) => {
      if (!MUSIC_TYPE_ORDER[a.key] || !MUSIC_TYPE_ORDER[b.key]) return 0
      if (MUSIC_TYPE_ORDER[a.key] < MUSIC_TYPE_ORDER[b.key]) return -1
      return 1
    })
}

export const aggregationToFacet = (
  key: string,
  aggregation: Aggregation[],
  subFacet?: boolean
): Facet | null => {
  const filterOpts = FILTERS[key]
  if (!subFacet && (!filterOpts || filterOpts.order === null)) return null

  return {
    ...filterOpts,
    category: key,
    terms: getTerms(aggregation),
  }
}

// Converts aggregations that have come back from ES to a data structure that can be read by the Filter component
export const mapAggregationsToFacets = (aggregations: Aggregations): Facet[] => {
  const facets: Facet[] = []

  for (const category of Object.keys(aggregations)) {
    const facet = aggregationToFacet(category, aggregations[category])
    if (!facet) continue

    facets.push(facet)
  }

  // Sort facets
  return facets.sort((a, b) => {
    if (a.order === null || b.order === null) return 0
    if (a.order < b.order) return -1
    return 1
  })
}

/**
 * Converts a given facet or category string into a valid category to be used in a URL parameter.
 * This function performs the opposite of {@link getAggregationCategory()}
 *
 * @param facet The facet or category to convert
 *
 * @example
 * getParamCategory('format') == 'format_type'
 * getParamCategory({ category: 'genres', terms: [] }) == 'genres'
 */
export const getParamCategory = (facet: Facet | string): string => {
  let category: string
  if (typeof facet === 'string') category = facet
  else category = facet.category
  return category === AggregationTerm.FORMAT ? 'format_type' : category
}

/**
 * Converts the given category into a valid category that is used with ES aggregations. This function
 * performs the opposite of {@link getParamCategory()}
 *
 * @param category
 *
 * @example
 * getAggregationCategory('format_type') == 'format'
 * getAggregationCategory('genres') == 'genres'
 */
export const getAggregationCategory = (category: string): string => {
  return category === 'format_type' ? AggregationTerm.FORMAT : category
}

export const hasSubFacets = (category: string, key: string): boolean => {
  return SUBFACETS[category] && SUBFACETS[category][key]
}

export const getSubFacetCategory = (category: string, key: string): string | null => {
  const aggCategory = getAggregationCategory(category)
  if (hasSubFacets(aggCategory, key)) return SUBFACETS[aggCategory][key]
  return null
}

export const isValidFilter = (key: string, allowTerm?: boolean): boolean =>
  !!FILTERS[getAggregationCategory(key)] || (!!allowTerm && key === 'term')

/**
 * Determines if the given category and value were computed from the page's slug filter. The slug
 * filter is computer on the backend and sent to the frontend in the API response
 *
 * @param category The filter category to check
 * @param value The value in the filter category
 * @param slugFilter The slug filter (if any) from the backend API response
 */
export const isSlugFilter = (category: string, value: string, slugFilter?: SlugFilter): boolean => {
  if (!slugFilter) return false
  return (
    slugFilter.category === getAggregationCategory(category) &&
    slugFilter.value.toString() === value
  )
}

export const facetTermDisplayText = (category: string, key: string, txPrefix?: string): string => {
  if (category === AggregationTerm.OPENING_KEY) return openingKeyDisplayText(key)
  return i18n.t(`${txPrefix ?? category}.${key}`)
}

const openingKeyDisplayText = (openingKey: string): string => {
  const [tonic, mode] = openingKey.split('/')
  if (tonic.toLowerCase() === 'atonal') {
    return i18n.t('opening_key.atonal')
  }

  let key = tonic
  if (mode) {
    key += ' ' + i18n.t(`modes.${mode.toLowerCase()}`)
  }
  return key
}
